<script setup lang="ts" generic="V extends string">
import { useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

interface ToggleOption {
    value: V;
    label: string;
    icon?: object;
    badge?: string | number;
    dot?: boolean;
}

interface Props {
    options: ToggleOption[];
    activeColor?: string;
    size?: 'sm' | 'md' | 'lg';
    shape?: 'pill' | 'square';
    fullWidth?: boolean;
    itemClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
    activeColor: 'bg-primary shadow-primary/25',
    size: 'md',
    shape: 'pill',
    fullWidth: false,
    itemClass: '',
});

const model = defineModel<V>({ required: true });

const activeIndex = computed(() => props.options.findIndex((o) => o.value === model.value));

const scrollerRef = ref<HTMLElement | null>(null);
const itemRefs = ref<HTMLElement[]>([]);

const setItemRef = (el: Element | null, index: number): void => {
    if (el instanceof HTMLElement) {
        itemRefs.value[index] = el;
    }
};

/**
 * Equal-segment fallback used before the buttons are laid out (and in
 * non-DOM test environments where `offsetWidth` is 0).
 */
const fallbackPillStyle = computed(() => {
    const segmentWidth = 100 / props.options.length;
    const index = activeIndex.value;
    return {
        width: `calc(${segmentWidth}% - 4px)`,
        left: index === 0 ? '4px' : `calc(${segmentWidth * index}% + 2px)`,
    };
});

// Measured from the active button so the pill tracks real (content-width) labels
// instead of assuming every segment is an equal slice of the container.
const measuredPillStyle = ref<{ width: string; left: string } | null>(null);

const updatePill = (): void => {
    const el = itemRefs.value[activeIndex.value];

    if (!el || el.offsetWidth === 0) {
        return;
    }

    measuredPillStyle.value = { width: `${el.offsetWidth}px`, left: `${el.offsetLeft}px` };
};

const pillStyle = computed(() => measuredPillStyle.value ?? fallbackPillStyle.value);

/**
 * Scroll affordances. Items are never allowed to shrink below their label
 * (`whitespace-nowrap`), so a bar with more tabs than fit would previously
 * spill outside its rounded container. The track scrolls instead, and these
 * flags drive the edge fades that hint there is more either side.
 */
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollAffordances = (): void => {
    const scroller = scrollerRef.value;

    if (!scroller) {
        return;
    }

    // `- 1` absorbs sub-pixel rounding, which would otherwise leave the right
    // fade permanently painted on a bar that is actually scrolled to its end.
    canScrollLeft.value = scroller.scrollLeft > 1;
    canScrollRight.value = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
};

/**
 * Keep the active tab visible when it is selected from outside the bar (the
 * review tree sidebar deep-links straight to a tab, which may sit off-screen).
 * Scrolls the track itself rather than calling `scrollIntoView`, which would
 * also scroll every ancestor and yank the surrounding page.
 */
const scrollActiveIntoView = (): void => {
    const scroller = scrollerRef.value;
    const el = itemRefs.value[activeIndex.value];

    if (!scroller || !el || scroller.scrollWidth <= scroller.clientWidth) {
        return;
    }

    const left = el.offsetLeft;
    const right = left + el.offsetWidth;
    const padding = 4;

    if (left < scroller.scrollLeft) {
        scroller.scrollLeft = left - padding;
    } else if (right > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = right - scroller.clientWidth + padding;
    }
};

const refreshLayout = (): void => {
    updatePill();
    updateScrollAffordances();
};

onMounted(() => nextTick(refreshLayout));
watch([activeIndex, () => props.options], () =>
    nextTick(() => {
        updatePill();
        scrollActiveIntoView();
        updateScrollAffordances();
    }),
);
useResizeObserver(scrollerRef, refreshLayout);

const sizeClasses = computed(() => {
    const map = {
        sm: 'px-4 py-1.5 text-xs',
        md: 'px-6 py-2.5 text-sm sm:px-8 sm:py-3',
        lg: 'px-8 py-3 text-base sm:px-10 sm:py-3.5',
    };
    return map[props.size];
});

const containerRadius = computed(() => (props.shape === 'square' ? 'rounded-none' : 'rounded-full'));
const itemRadius = computed(() => (props.shape === 'square' ? 'rounded-none' : 'rounded-full'));
const containerLayout = computed(() => (props.fullWidth ? 'flex w-full items-stretch' : 'inline-flex items-center'));
const itemLayout = computed(() => (props.fullWidth ? 'flex-1 justify-center' : ''));
const shellLayout = computed(() => (props.fullWidth ? 'relative w-full' : 'relative inline-block max-w-full'));
const fadeRadius = computed(() => (props.shape === 'square' ? '' : 'rounded-full'));
</script>

<template>
    <div :class="shellLayout">
        <div
            ref="scrollerRef"
            class="no-scrollbar relative overflow-x-auto border border-border/80 bg-card/70 p-1 shadow-lg shadow-foreground/4 backdrop-blur-md"
            :class="[containerLayout, containerRadius]"
            role="tablist"
            @scroll.passive="updateScrollAffordances"
        >
            <div
                class="ease-in-out absolute inset-y-1 shadow-md transition-all duration-500"
                :class="[activeColor, itemRadius]"
                :style="pillStyle"
                aria-hidden="true"
            />

            <button
                v-for="(option, index) in options"
                :key="option.value"
                :ref="(el) => setItemRef(el as Element | null, index)"
                type="button"
                role="tab"
                :aria-selected="model === option.value"
                class="relative z-10 flex items-center gap-2.5 font-medium tracking-wide transition-colors duration-300"
                :class="[
                    sizeClasses,
                    itemRadius,
                    itemLayout,
                    itemClass,
                    model === option.value ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                ]"
                @click="model = option.value"
            >
                <component :is="option.icon" v-if="option.icon" class="h-4 w-4 shrink-0" />
                <span class="whitespace-nowrap">{{ option.label }}</span>
                <span v-if="option.dot" class="relative flex size-2.5 shrink-0" aria-hidden="true">
                    <span
                        class="absolute inline-flex size-full animate-ping rounded-full opacity-75"
                        :class="model === option.value ? 'bg-primary-foreground' : 'bg-warning'"
                    />
                    <span
                        class="relative inline-flex size-2.5 rounded-full transition-colors duration-300"
                        :class="model === option.value ? 'bg-primary-foreground' : 'bg-warning'"
                    />
                </span>
                <span
                    v-if="option.badge !== undefined"
                    class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold transition-colors duration-300"
                    :class="model === option.value ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'"
                >
                    {{ option.badge }}
                </span>
            </button>
        </div>

        <!-- Edge fades: only painted when there is actually more track that way. -->
        <div
            v-show="canScrollLeft"
            class="from-card pointer-events-none absolute inset-y-1 left-1 w-8 bg-gradient-to-r to-transparent"
            :class="fadeRadius"
            data-testid="segmented-toggle-fade-left"
            aria-hidden="true"
        />
        <div
            v-show="canScrollRight"
            class="from-card pointer-events-none absolute inset-y-1 right-1 w-8 bg-gradient-to-l to-transparent"
            :class="fadeRadius"
            data-testid="segmented-toggle-fade-right"
            aria-hidden="true"
        />
    </div>
</template>
